<template>
  <div class="sign-in">
    <v-form @submit.prevent=""
            id="signInForm"
            v-model="form.valid" ref="form">
      <fin-email-input v-model="form.fields.email"
                       :disabled="disabled"
                       ref="emailInput"
                       :placeholder="autofilled ? ' ': null"
                       id="mail"
                       validate-on-blur
                       :append-icon="`${useBiometry ? 'mdi-fingerprint': ''}`"
                       @input="validate"
                       :click-append="biometricAuth"
      />
      <fin-password-input v-model="form.fields.password"
                          :disabled="disabled"
                          :placeholder="autofilled ? ' ': null"
                          @input="validate"
                          id="pwd"/>
      <div v-if="form.errors">
        <template v-if="Array.isArray(form.errors.msg)">
          <fin-error-msg v-for="(m, index) in form.errors.msg"
                         :key="index"
                         :msg="m"/>
        </template>
        <fin-error-msg v-else
                       :msg="form.errors.msg"/>
      </div>

      <div>
        <v-btn id="signInBtn"
               :disabled="(disabled || !form.valid || form.fields.password === '') && !autofilled"
               :loading="loading"
               color="fin-color-primary-dark"
               :class="$vuetify.breakpoint.xsOnly ? 'mb-3' : ''"
               :width="buttonWidth"
               outlined
               type="submit"
               @click="submit({email: form.fields.email, password: form.fields.password})">
          Anmelden
        </v-btn>
        <v-btn
          v-if="allowPwReset && (form.errors ? form.errors.error.code !== 'UserNotConfirmedException' : true)"
          id="forgotPwdBtn"
          color="fin-color-primary-dark"
          :width="buttonWidth"
          text
          @click="$emit('forgotPassword')">
          Passwort vergessen
        </v-btn>
        <v-btn v-if="form.errors && form.errors.error.code === 'UserNotConfirmedException'"
               :disabled="!isActive"
               color="fin-color-primary-dark"
               :width="buttonWidth"
               text
               @click="resendVerificationCode">
          Verifikation erneut zusenden
        </v-btn>
      </div>

      <div v-if="!isActive" class="mt-3">
        <fin-info-msg :msg="info"/>
        <p style="text-align: center">Nichts angekommen? Überprüfe auch deinen
          Spam-Ordner.</p>
      </div>
      <fin-social-media-sign-in v-if="isSocialMediaAppleActive || isSocialMediaGoogleActive"
                                :isSocialMediaAppleActive="isSocialMediaAppleActive"
                                :isSocialMediaGoogleActive="isSocialMediaGoogleActive"
                                @socialLoginInProgress="(val) => $emit('socialLoginInProgress', val)">
      </fin-social-media-sign-in>
    </v-form>
  </div>
</template>

<script>
import finEmailInput from '@/components/common/form/EmailInput'
import finPasswordInput from '@/components/common/form/PasswordInput'
import {mapActions, mapGetters, mapState} from 'vuex'
import FinInfoMsg from "@/components/common/InfoMsg"
import {ERROR_TYPE, handleError} from '@/js/errorhandler/finAWSErrorHandler'
import FinErrorMsg from "@/components/common/ErrorMsg"
import finBrowser from "@/js/finBrowser"
import {NativeBiometric} from "capacitor-native-biometric"

// configure file aws-export depending on platform and environment for social media login
import aws_config from '@/aws-exports'
import * as AmplifyModules from "@/plugins/finAmplifyModules"
import FinSocialMediaSignIn from "@/components/auth/SocialMediaSignIn"
// import {Amplify} from "@/plugins/finAmplifyModules"
// Amplify.Logger.LOG_LEVEL = 'DEBUG'
// set the exact same values in redirectSignIn/redirectSignOut as in the AWS Cognito SignIn/SignOut Redirects
aws_config.oauth.redirectSignIn = finBrowser.IS_IOS_APP ? "capacitor://localhost/" :
  finBrowser.IS_ANDROID_APP ? "com.fincraft://signin/" : aws_config.oauth.redirectSignIn
aws_config.oauth.redirectSignOut = finBrowser.IS_IOS_APP ? "capacitor://localhost/" :
  finBrowser.IS_ANDROID_APP ? "com.fincraft/signout/" : aws_config.oauth.redirectSignOut
AmplifyModules.Auth.configure(aws_config)

export default {
  name: 'FinSignIn',
  components: {
    FinSocialMediaSignIn,
    FinErrorMsg,
    FinInfoMsg,
    finPasswordInput,
    finEmailInput
  },
  props: {
    isSocialMediaAppleActive: {
      type: Boolean,
      default: false
    },
    isSocialMediaGoogleActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      autofilled: false,
      firstSignIn: true,
      isIosOrAndroidApp: finBrowser.IS_IOS_OR_ANDROID_APP,
      isIOS: finBrowser.IS_IOS_APP,
      biometricAvailableOnDevice: undefined,
      loading: false,
      isActive: true,
      isSocialAppleActive: false,
      isSocialGoogleActive: false,
      biometricAuthSwitchStatus: false,
      info: '',
      form: {
        valid: false,
        fields: {
          email: process.env.VUE_APP_DEFAULT_USER,
          password: process.env.VUE_APP_DEFAULT_PASSWORD,
        },
        errors: null,
      },
      disabled: process.env.VUE_APP_ALLOW_SIGN_IN !== 'true',
      allowPwReset: process.env.VUE_APP_ALLOW_PASSWORD_RESET === 'true'
    }
  },
  computed: {
    useBiometry() {
      return this.biometricAuthSwitchStatus && this.biometricAvailableOnDevice
    },
    buttonWidth() {
      let width = ""
      if (this.$vuetify.breakpoint.xs) {
        width = "100%"
      }
      return width
    },
    ...mapState({
      profile: state => state.auth.profile
    }),
    ...mapGetters('auth', ['user', 'user'])
  },
  methods: {
    ...mapActions('auth', ['signIn']),
    setError(e) {
      this.loading = false

      let error = handleError(ERROR_TYPE.AUTH, e)
      let errorMsg = ""
      if (Array.isArray(error)) {
        errorMsg = error[0].msg
      } else {
        errorMsg = error.msg
      }

      this.form.errors = {
        error: e,
        msg: errorMsg
      }

      console.error('Signin Error', e)
    },
    async submit({email, password}) {
      //FINDEV-3309: Safari Cognito Bug (QuotaExceededError: The quota has been exceeded.) - Workaround for technical error on Macs and iPhones if localStorage is not cleared
      if (this.isIOS) {
        const localStorageItems = {...localStorage}
        window.localStorage.clear()
        for (const key in localStorageItems) {
          window.localStorage.setItem(key, localStorageItems[key])
        }
      }

      email = email.trim()
      password = password.trim()
      this.form.fields.email = email
      this.form.fields.password = password
      if (this.disabled) {
        // should not happen, but protect this case
        console.log('should not happen, but just in case..., disabled:', this.disabled)
        return
      }
      this.loading = true

      try {
        if (this.biometricAvailableOnDevice) {
          await NativeBiometric.deleteCredentials({server: "fincraft"})
          // Set user's credentials for biometric auth
          await NativeBiometric.setCredentials({
            username: email,
            password: password,
            server: "fincraft",
          })

          if (this.firstSignIn) {
            await this.useBiometricAuthQuestion()
          }
        }
      } catch (e) {
        console.error("error setting new Credentials", e)
      }

      try {
        await this.signIn({
          email: email,
          password: password
        })

        this.loading = false

        // prevent Uncaught (in promise) Error: Redirected when going from "/onboarding/fasttrack" to "/protected/myFinHub" via a navigation guard
        // see https://stackoverflow.com/questions/62223195/vue-router-uncaught-in-promise-error-redirected-from-login-to-via-a
        //
        // as the router guards can execute other redirects
        // add .catch(() => {})
        if (this.$route.query.redirect) {
          this.$router.push({path: this.$route.query.redirect}).catch(() => {
          })
        } else {
          this.$router.push({name: 'myFinHub'}).catch(() => {
          })
        }
      } catch (e) {
        this.setError(e)
      }
    },
    resendVerificationCode() {
      // TODO remove skip code??
      if (this.form.fields.email && this.form.fields.email.length > 0) {
        this.$Amplify.Auth.resendSignUp(this.form.fields.email, {
          preferredLang: 'de_DE',
          signUpHost: window.location.origin
        }).then(() => {
          this.info = 'Ein neuer Verifikations-Code wurde dir zugeschickt.'
          this.isActive = false
          this.form.errors = null
        }).catch(this.setError)
      }
    },
    async useBiometricAuthQuestion() {
      localStorage.setItem(finBrowser.BIOMETRIC_AUTH_KEY, 'false')
      try {
        await NativeBiometric.verifyIdentity({
          reason: "Möchtest du die biometrische Authentifizierung dauerhaft zur Anmeldung nutzen?",
          title: "Login",
          subtitle: "Möchtest du die biometrische Authentifizierung dauerhaft zur Anmeldung nutzen?",
          description: "",
          negativeButtonText: "Nein"
        })
        localStorage.setItem(finBrowser.BIOMETRIC_AUTH_KEY, 'true')
      } catch (e) {
        console.log("authenticate failed", e)
      }
    },
    async biometricAuth() {
      try {
        const credentials = await NativeBiometric.getCredentials({
          server: "fincraft",
        })
        try {
          if (this.biometricAuthSwitchStatus) {
            await NativeBiometric.verifyIdentity({
              reason: "Login",
              title: "Login",
              subtitle: "",
              description: "",
              negativeButtonText: "Abbrechen"
            })
            await this.submit({email: credentials.username, password: credentials.password})
          }
        } catch (e) {
          console.log("authenticate failed", e)
        }
      } catch (e) {
        console.log("no credentials", e)
      }
    },
    validate() {
      // check form validity
      if (this.$refs.form) {
        this.$refs.form.validate()
      }
    }
  },
  async mounted() {
    let emailInput = this.$refs.emailInput.$el.querySelector('input')
    setTimeout(() => {
      try {
        if (emailInput.matches(':-internal-autofill-selected')) {
          if (!this.form.fields.email) { // no need for autofill workaround if email is already known
            this.autofilled = true
          }
        }
      } catch (e) {
        // ignore error, error thrown if not using chrome
      }
    }, 500)

    if (finBrowser.IS_IOS_OR_ANDROID_APP) {
      // trying to retrieve credentials of users who have not logged out and are using biometric login
      try {
        const result = await NativeBiometric.isAvailable()
        this.biometricAvailableOnDevice = result.isAvailable
        if (this.biometricAvailableOnDevice) {
          this.biometricAuthSwitchStatus = (localStorage.getItem(finBrowser.BIOMETRIC_AUTH_KEY) === 'true') || false
          try {
            await NativeBiometric.getCredentials({server: "fincraft"})
            this.firstSignIn = false

            if (this.biometricAvailableOnDevice && this.biometricAuthSwitchStatus && this.$route.query?.userHasLoggedOut !== "true") {
              await this.biometricAuth()
            }
          } catch (e) {
            this.firstSignIn = true
          }
        }
      } catch (e) {
        console.error("smartphone doesn't respond", e)
      }
    }
  },
  watch: {
    'form.fields.email': {
      handler(value) {
        if (value) {
          this.autofilled = false
        }
      },
      immediate: true
    },
  }
}
</script>

<style scoped lang="scss">
::v-deep .v-input__append-inner {
  margin-top: 15px !important;
}
</style>
